import { supabase } from '../lib/supabase';
import type {
  EmailTemplateDraft,
  LinkedInTemplateDraft,
  CallScriptDraft,
  CallAgentSettings,
} from '../types/settings';
import { getLocalUser } from '../utils/authHelpers';

export type TemplateStatus = 'draft' | 'approved' | 'not_configured';

export async function getEmailDrafts(): Promise<EmailTemplateDraft[]> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) return [];

    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('client_id', localUser.client_id)
      .eq('status', 'draft')
      .order('template_key', { ascending: true });

    if (error) throw error;
    return (data || []) as EmailTemplateDraft[];
  } catch (error) {
    console.error('Error fetching email drafts:', error);
    return [];
  }
}

export async function getLinkedInDrafts(): Promise<LinkedInTemplateDraft[]> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) return [];

    const { data, error } = await supabase
      .from('linkedin_templates')
      .select('*')
      .eq('client_id', localUser.client_id)
      .eq('status', 'draft')
      .order('template_key', { ascending: true });

    if (error) throw error;
    return (data || []) as LinkedInTemplateDraft[];
  } catch (error) {
    console.error('Error fetching LinkedIn drafts:', error);
    return [];
  }
}

export async function getCallScriptDrafts(): Promise<CallScriptDraft | null> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) return null;

    const { data, error } = await supabase
      .from('call_scripts')
      .select('*')
      .eq('client_id', localUser.client_id)
      .in('status', ['draft', 'approved'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data as CallScriptDraft | null;
  } catch (error) {
    console.error('Error fetching call script drafts:', error);
    return null;
  }
}

export async function getWhatsAppDrafts(): Promise<Array<{ template_key: string; submitted_text: string; submitted_button_text: string; submitted_button_url: string; source: string }>> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) return [];

    const { data, error } = await supabase
      .from('whatsapp_templates')
      .select('template_key, submitted_text, submitted_button_text, submitted_button_url, source')
      .eq('client_id', localUser.client_id)
      .eq('status', 'draft')
      .neq('source', 'system')
      .order('template_key', { ascending: true });

    if (error) throw error;
    return (data || []).map(d => ({ ...d, source: d.source || 'manual' }));
  } catch (error) {
    console.error('Error fetching WhatsApp drafts:', error);
    return [];
  }
}

export const CALL_SCRIPT_SECTIONS = [
  'script_1_greetings',
  'script_1_introduction',
  'script_1_why_we_are_calling',
  'script_1_elaboration_credibility',
  'script_1_engagement',
  'script_1_qualification',
  'script_1_next_steps',
  'script_1_booking',
  'script_1_closing',
  'script_2_greetings',
  'script_2_introduction',
  'script_2_why_we_are_calling_again',
  'script_2_engagement',
  'script_2_qualification_if_not_done',
  'script_2_next_steps',
  'script_2_booking',
  'script_2_closing',
  'objection_handling',
] as const;

export type CallScriptSection = typeof CALL_SCRIPT_SECTIONS[number];

export async function getEmailTemplateStatuses(): Promise<Record<string, TemplateStatus>> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) return {};

    const { data, error } = await supabase
      .from('email_templates')
      .select('template_key, status')
      .eq('client_id', localUser.client_id)
      .in('status', ['draft', 'approved'])
      .order('created_at', { ascending: false });

    if (error) throw error;

    const statuses: Record<string, TemplateStatus> = {};
    for (const row of (data || [])) {
      if (!statuses[row.template_key]) {
        statuses[row.template_key] = row.status as TemplateStatus;
      }
    }
    return statuses;
  } catch (error) {
    console.error('Error fetching email template statuses:', error);
    return {};
  }
}

export async function getLinkedInTemplateStatuses(): Promise<Record<string, TemplateStatus>> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) return {};

    const { data, error } = await supabase
      .from('linkedin_templates')
      .select('template_key, status')
      .eq('client_id', localUser.client_id)
      .in('status', ['draft', 'approved'])
      .order('created_at', { ascending: false });

    if (error) throw error;

    const statuses: Record<string, TemplateStatus> = {};
    for (const row of (data || [])) {
      if (!statuses[row.template_key]) {
        statuses[row.template_key] = row.status as TemplateStatus;
      }
    }
    return statuses;
  } catch (error) {
    console.error('Error fetching LinkedIn template statuses:', error);
    return {};
  }
}

export async function getCallScriptSectionStatuses(): Promise<Record<string, TemplateStatus>> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) return {};

    const { data, error } = await supabase
      .from('call_scripts')
      .select('status, script_data')
      .eq('client_id', localUser.client_id)
      .in('status', ['draft', 'approved'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    if (!data) return {};

    if (data.status === 'approved') {
      const allApproved: Record<string, TemplateStatus> = {};
      for (const s of CALL_SCRIPT_SECTIONS) {
        allApproved[s] = 'approved';
      }
      return allApproved;
    }

    return (data.script_data?.section_statuses || {}) as Record<string, TemplateStatus>;
  } catch (error) {
    console.error('Error fetching call script statuses:', error);
    return {};
  }
}

export async function saveEmailTemplatesAsDrafts(
  templates: Record<string, { subject: string; body: string }>,
  statuses: Record<string, TemplateStatus>,
): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) throw new Error('Not authenticated');

    for (const [key, tmpl] of Object.entries(templates)) {
      if (statuses[key] !== 'draft') continue;
      if (!tmpl.subject && !tmpl.body) continue;

      const { data: existing } = await supabase
        .from('email_templates')
        .select('id')
        .eq('client_id', localUser.client_id)
        .eq('user_id', localUser.email)
        .eq('template_key', key)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        await supabase.from('email_templates').update({
          subject_text: tmpl.subject,
          body_text: tmpl.body,
          status: 'draft',
          source: 'manual',
        }).eq('id', existing.id);
      } else {
        await supabase.from('email_templates').insert({
          user_id: localUser.email,
          client_id: localUser.client_id,
          template_key: key,
          subject_text: tmpl.subject,
          body_text: tmpl.body,
          source: 'manual',
          status: 'draft',
        });
      }
    }
    return true;
  } catch (error) {
    console.error('Error saving email templates as drafts:', error);
    return false;
  }
}

export async function saveLinkedInTemplatesAsDrafts(
  templates: Record<string, { text: string }>,
  statuses: Record<string, TemplateStatus>,
): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) throw new Error('Not authenticated');

    for (const [key, tmpl] of Object.entries(templates)) {
      if (statuses[key] !== 'draft') continue;
      if (!tmpl.text) continue;

      const { data: existing } = await supabase
        .from('linkedin_templates')
        .select('id')
        .eq('client_id', localUser.client_id)
        .eq('user_id', localUser.email)
        .eq('template_key', key)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        await supabase.from('linkedin_templates').update({
          message_text: tmpl.text,
          step_name: key,
          status: 'draft',
          source: 'manual',
        }).eq('id', existing.id);
      } else {
        await supabase.from('linkedin_templates').insert({
          user_id: localUser.email,
          client_id: localUser.client_id,
          template_key: key,
          step_name: key,
          message_text: tmpl.text,
          source: 'manual',
          status: 'draft',
        });
      }
    }
    return true;
  } catch (error) {
    console.error('Error saving LinkedIn templates as drafts:', error);
    return false;
  }
}

export async function saveCallScriptAsDraft(
  scriptData: any,
  sectionStatuses: Record<string, TemplateStatus>,
): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) throw new Error('Not authenticated');

    const hasDrafts = Object.values(sectionStatuses).some(s => s === 'draft');
    if (!hasDrafts) return true;

    const payload = {
      script_1: scriptData.script_1,
      script_2: scriptData.script_2,
      objection_handling: scriptData.objection_handling,
      section_statuses: sectionStatuses,
    };

    const { data: existing } = await supabase
      .from('call_scripts')
      .select('id')
      .eq('client_id', localUser.client_id)
      .eq('user_id', localUser.email)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing) {
      await supabase.from('call_scripts').update({
        script_data: payload,
        status: 'draft',
        source: 'manual',
      }).eq('id', existing.id);
    } else {
      await supabase.from('call_scripts').insert({
        user_id: localUser.email,
        client_id: localUser.client_id,
        script_data: payload,
        source: 'manual',
        status: 'draft',
      });
    }
    return true;
  } catch (error) {
    console.error('Error saving call script as draft:', error);
    return false;
  }
}

export async function saveWhatsAppTemplatesAsDrafts(
  templates: Record<string, { text: string; button_text?: string; button_url?: string }>,
): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) throw new Error('Not authenticated');

    for (const [key, tmpl] of Object.entries(templates)) {
      if (!tmpl.text) continue;

      const { data: existing } = await supabase
        .from('whatsapp_templates')
        .select('id, status')
        .eq('client_id', localUser.client_id)
        .eq('user_id', localUser.email)
        .eq('template_key', key)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing && (existing.status === 'pending_review' || existing.status === 'approved')) continue;

      if (existing) {
        await supabase.from('whatsapp_templates').update({
          submitted_text: tmpl.text,
          submitted_button_text: tmpl.button_text || '',
          submitted_button_url: tmpl.button_url || '',
          status: 'draft',
          source: 'manual',
        }).eq('id', existing.id);
      } else {
        await supabase.from('whatsapp_templates').insert({
          user_id: localUser.email,
          client_id: localUser.client_id,
          template_key: key,
          submitted_text: tmpl.text,
          submitted_button_text: tmpl.button_text || '',
          submitted_button_url: tmpl.button_url || '',
          status: 'draft',
          source: 'manual',
        });
      }
    }
    return true;
  } catch (error) {
    console.error('Error saving WhatsApp templates as drafts:', error);
    return false;
  }
}

export async function approveSingleEmailTemplate(
  templateKey: string,
  subjectText: string,
  bodyText: string,
): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) throw new Error('Not authenticated');

    const { data: existing } = await supabase
      .from('email_templates')
      .select('id')
      .eq('client_id', localUser.client_id)
      .eq('user_id', localUser.email)
      .eq('template_key', templateKey)
      .in('status', ['draft', 'approved'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing) {
      await supabase.from('email_templates').update({
        subject_text: subjectText,
        body_text: bodyText,
        status: 'approved',
        source: 'manual',
      }).eq('id', existing.id);
    } else {
      await supabase.from('email_templates').insert({
        user_id: localUser.email,
        client_id: localUser.client_id,
        template_key: templateKey,
        subject_text: subjectText,
        body_text: bodyText,
        source: 'manual',
        status: 'approved',
      });
    }

    // Sync directly to agents_settings.templates for immediate consistency
    const { data: agentRow } = await supabase
      .from('agents_settings')
      .select('agent_id, templates')
      .eq('client_id', localUser.client_id)
      .eq('agent_type', 'email')
      .maybeSingle();

    if (agentRow?.agent_id) {
      const currentTemplates = agentRow.templates || {};
      await supabase.from('agents_settings').update({
        templates: {
          ...currentTemplates,
          [templateKey]: { subject: subjectText, body: bodyText },
        },
        updated_at: new Date().toISOString(),
      }).eq('agent_id', agentRow.agent_id);
    }

    return true;
  } catch (error) {
    console.error('Error approving single email template:', error);
    return false;
  }
}

export async function approveSingleLinkedInTemplate(
  templateKey: string,
  messageText: string,
): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) throw new Error('Not authenticated');

    const { data: existing } = await supabase
      .from('linkedin_templates')
      .select('id')
      .eq('client_id', localUser.client_id)
      .eq('user_id', localUser.email)
      .eq('template_key', templateKey)
      .in('status', ['draft', 'approved'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing) {
      await supabase.from('linkedin_templates').update({
        message_text: messageText,
        step_name: templateKey,
        status: 'approved',
        source: 'manual',
      }).eq('id', existing.id);
    } else {
      await supabase.from('linkedin_templates').insert({
        user_id: localUser.email,
        client_id: localUser.client_id,
        template_key: templateKey,
        step_name: templateKey,
        message_text: messageText,
        source: 'manual',
        status: 'approved',
      });
    }

    // Sync directly to agents_settings.templates for immediate consistency
    const { data: agentRow } = await supabase
      .from('agents_settings')
      .select('agent_id, templates')
      .eq('client_id', localUser.client_id)
      .eq('agent_type', 'linkedin')
      .maybeSingle();

    if (agentRow?.agent_id) {
      const currentTemplates = agentRow.templates || {};
      await supabase.from('agents_settings').update({
        templates: {
          ...currentTemplates,
          [templateKey]: { text: messageText },
        },
        updated_at: new Date().toISOString(),
      }).eq('agent_id', agentRow.agent_id);
    }

    return true;
  } catch (error) {
    console.error('Error approving single LinkedIn template:', error);
    return false;
  }
}

export async function approveSingleCallScriptSection(
  section: CallScriptSection,
  sectionData: any,
  fullSettings: CallAgentSettings,
): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) throw new Error('Not authenticated');

    const { data: existing } = await supabase
      .from('call_scripts')
      .select('id, script_data')
      .eq('client_id', localUser.client_id)
      .eq('user_id', localUser.email)
      .in('status', ['draft', 'approved'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    const currentData = existing?.script_data ? { ...existing.script_data } : {};
    const currentSectionStatuses = currentData.section_statuses ? { ...currentData.section_statuses } : {};

    if (section === 'objection_handling') {
      currentData.objection_handling = sectionData;
    } else {
      const scriptNum = section.startsWith('script_1_') ? 'script_1' : 'script_2';
      const fieldName = section.slice(scriptNum.length + 1);

      if (!currentData[scriptNum]) currentData[scriptNum] = {};
      currentData[scriptNum][fieldName] = sectionData;
    }

    currentSectionStatuses[section] = 'approved';
    currentData.section_statuses = currentSectionStatuses;

    const allApproved = CALL_SCRIPT_SECTIONS.every(s => currentSectionStatuses[s] === 'approved');
    const newStatus = allApproved ? 'approved' : 'draft';

    if (existing) {
      await supabase.from('call_scripts').update({
        script_data: currentData,
        status: newStatus,
        source: 'manual',
      }).eq('id', existing.id);
    } else {
      await supabase.from('call_scripts').insert({
        user_id: localUser.email,
        client_id: localUser.client_id,
        script_data: currentData,
        source: 'manual',
        status: newStatus,
      });
    }

    if (allApproved) {
      // Sync to agents_settings.templates for immediate consistency
      const { data: agentRow } = await supabase
        .from('agents_settings')
        .select('agent_id')
        .eq('client_id', localUser.client_id)
        .eq('agent_type', 'call')
        .maybeSingle();

      if (agentRow?.agent_id) {
        await supabase.from('agents_settings').update({
          templates: currentData,
          updated_at: new Date().toISOString(),
        }).eq('agent_id', agentRow.agent_id);
      }
    }

    return true;
  } catch (error) {
    console.error('Error approving call script section:', error);
    return false;
  }
}
